import '../../css/app.css';

import { createInertiaApp, router } from '@inertiajs/react';
import * as Sentry from '@sentry/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'sonner';

import { onSessionExpired, onUnauthenticated } from '@/shared/api/apiClient';
import { isReadOnlyDenial, openReadOnlyDialog } from '@/shared/api/readOnly';
import { PaletteProvider } from '@/widgets/command-palette';
import './i18n';
import { EchoProvider } from './providers/EchoProvider';
import { NotificationsProvider } from './providers/NotificationsProvider';
import { ThemeProvider } from './providers/ThemeProvider';

// Recover globally from an expired session / rotated CSRF token on Inertia
// requests (form.post, router.*). The custom-fetch path is handled in apiFetch.
// Note: Inertia v3 uses "httpException" (not "invalid" from v1) for non-Inertia
// HTTP responses; the detail.response is typed as HttpResponse with a .status field.
//
// 419 means the token rotated under us — a reload picks up a fresh one. 401 means
// the session is gone, so reloading would only bounce through auth; go to login
// instead. The server recorded url.intended on its way out, so signing in returns
// the user here.
router.on('httpException', (event) => {
    const status = event.detail.response?.status;

    if (status === 419) {
        event.preventDefault(); // suppress Inertia's default modal; we own recovery
        onSessionExpired();
        return;
    }

    if (status === 401) {
        event.preventDefault();
        const data = event.detail.response?.data as { redirect?: string } | undefined;
        onUnauthenticated(typeof data?.redirect === 'string' ? data.redirect : undefined);
        return;
    }

    // EnforceReadOnly: show the read-only dialog instead of Inertia's error modal.
    if (status === 403 && isReadOnlyDenial(403, event.detail.response?.data) && openReadOnlyDialog()) {
        event.preventDefault();
    }
});

// Inertia rejects the visit promise on xhr.onerror — connection dropped, offline,
// tab torn down mid-flight. No HTTP response was ever received (4xx/5xx arrive as
// httpException instead), so there is nothing server-side to fix and nothing
// actionable per event. Nothing awaits router.visit(), so that rejection surfaces
// as an unhandledrejection and Sentry reports it at 100%. preventDefault() makes
// Inertia's fireNetworkErrorEvent() return false, so it returns instead of
// re-rejecting. Genuine outages are covered server-side, not from here.
router.on('networkError', (event) => {
    event.preventDefault();
});

const appName = import.meta.env.VITE_APP_NAME || 'Milpilots';

createInertiaApp({
    title: (title) => (title ? `${title} - ${appName}` : appName),
    resolve: (name) => resolvePageComponent(`../pages/${name}.tsx`, import.meta.glob('../pages/**/*.tsx')) as never,
    setup({ el, App, props }) {
        const dsn = import.meta.env.VITE_SENTRY_FRONTEND_DSN as string | undefined;
        if (dsn) {
            Sentry.init({
                dsn,
                tracesSampleRate: 0.2,
                environment: import.meta.env.MODE,
            });
        }

        const initialProps = props.initialPage.props as {
            auth?: { user?: { id?: number; role?: import('@/shared/config/feature').UserRole }; unread_notifications_count?: number };
            features?: { cmdk_enabled?: boolean };
        };
        const userId = typeof initialProps.auth?.user?.id === 'number' ? initialProps.auth.user.id : null;
        const cmdkEnabled = initialProps.features?.cmdk_enabled ?? false;
        const notificationRole = initialProps.auth?.user?.role ?? null;
        const initialUnread = Number(initialProps.auth?.unread_notifications_count ?? 0);

        createRoot(el).render(
            <ThemeProvider>
                <EchoProvider userId={userId}>
                    <NotificationsProvider role={notificationRole} initialUnreadCount={initialUnread}>
                        <PaletteProvider enabled={cmdkEnabled}>
                            <App {...props} />
                            <Toaster richColors closeButton />
                        </PaletteProvider>
                    </NotificationsProvider>
                </EchoProvider>
            </ThemeProvider>,
        );
    },
    progress: { color: '#4B5563' },
});
